import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent from "components/table/TableRowComponent";
import { HEADER_EST_PDF, HEADER_WHEEL_PDF, HEADER_WHEEL_TIRE_EST_PDF } from "constants/data";
import { QuoteTireDetail, QuoteWheelDetail } from "features/quotation/types";
import { Fragment } from "react/jsx-runtime";
import { formatCurrency, formatWheelRimDiamDisplay, getPriceDisplayText, isPriceLabel } from "utils";
import generateUniqueId from "util/generate_id";
import ImageComponent from "components/image/ImageComponent";
import Barcode from "components/Barcode";

const wheelImageStyle = {
  height: "146px",
  width: "auto",
  aspectRatio: "1/1",
};

interface TableWheelTireEstResultPdfProps {
  wheelData?: QuoteWheelDetail;
  tireData?: QuoteTireDetail[];
  optionsPrice: number;
}

/**
 * TableWheelTireEstResultPdf component renders a table with wheel and tire estimation data.
 * @param {TableWheelTireEstResultPdfProps} props - Component props
 * @param {QuoteWheelDetail} props.wheelData - Wheel detail information
 * @param {QuoteTireDetail[]} props.tireData - Array of tire details
 */
export default function TableWheelTireEstResultPdf({
  wheelData,
  tireData,
  optionsPrice,
}: TableWheelTireEstResultPdfProps) {
  // Render currency-like values (numbers, "-", "0", "無料") with ¥ and spacing, same layout as TableEstDiscount
  const renderPriceLabel = (value: string | number) => {
    const valueStr = String(value ?? "");
    if (!isPriceLabel(valueStr)) return valueStr;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box component="span">¥</Box>
        <Box component="p" sx={{ textAlign: "center", width: "100%", my: 0 }}>
          {valueStr}
        </Box>
      </Box>
    );
  };
  // Build wheel data array from API response
  const wheelDisplayData = wheelData
    ? [
      wheelData.maker_name || "-",
      `${wheelData.wheel_size} ${formatWheelRimDiamDisplay(wheelData.midw_wheel_hub_diam)}Φ `,
      `${wheelData.quantity || 0}本`,
      wheelData.fixed_discount ? `${wheelData.fixed_discount}%` : "0%",
      formatCurrency(optionsPrice || 0),
      wheelData.brand_name || "-",
      wheelData.wheel_color_name || "-",
      formatCurrency(wheelData.sale_price || 0),
      formatCurrency(wheelData.discounted_sale_price || 0),
      formatCurrency(wheelData.confirm_total_price || 0),
    ]
    : [];

  // Use tire data from API or empty array
  const tireList = tireData || [];
  // Don't render if no wheel data
  if (!wheelData) {
    return null;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", width: "100%", gap: 5 }}>
        <ImageComponent
          src={wheelData.image_url || "/img/wheel/wheel.svg"}
          alt=""
          sx={wheelImageStyle}
        />
        <Box sx={{ flex: 1, overflowX: "auto" }}>
          <Box sx={{ pb: "10px", borderBottom: "1px solid #707070" }}>
            <TableContainer component={Table} sx={{ boxShadow: "none", minWidth: 1480 }}>
              <TableBody>
                <TableRow>
                  {HEADER_WHEEL_PDF.slice(0, Math.ceil(HEADER_WHEEL_PDF.length / 2)).map(
                    (item, index) => (
                      <Fragment key={generateUniqueId(index)}>
                        <TableCellComponent type="header" sx={item.sx}>
                          <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }}>
                            {item.label}
                          </Typography>
                        </TableCellComponent>
                        <TableCellComponent sx={{ border: "unset", px: 1 }}>
                          <Typography
                            component="div"
                            sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }}
                          >
                            {renderPriceLabel(wheelDisplayData[index])}
                          </Typography>
                        </TableCellComponent>
                      </Fragment>
                    ),
                  )}
                </TableRow>
                <TableRow>
                  {HEADER_WHEEL_PDF.slice(
                    Math.ceil(HEADER_WHEEL_PDF.length / 2),
                    HEADER_WHEEL_PDF.length,
                  ).map((item, index) => (
                    <Fragment key={generateUniqueId(index)}>
                      <TableCellComponent type="header" sx={item.sx}>
                        <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }}>
                          {item.label}
                        </Typography>
                      </TableCellComponent>
                      <TableCellComponent sx={{ border: "unset", px: 1 }}>
                        <Typography
                          component="div"
                          sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }}
                        >
                          {renderPriceLabel(
                            wheelDisplayData[index + Math.ceil(HEADER_WHEEL_PDF.length / 2)],
                          )}
                        </Typography>
                      </TableCellComponent>
                    </Fragment>
                  ))}
                </TableRow>
              </TableBody>
            </TableContainer>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              my: 1,
            }}
          >
            <Box sx={{ minWidth: "150px", display: "flex", flexDirection: "column" }}>
              <Typography style={{ fontSize: "14px", textAlign: "left" }}>ホイールバーコード</Typography>
              {wheelData.midw_jan_cd && (
                <Barcode value={wheelData.midw_jan_cd} width={195} height={40} />
              )}
            </Box>
            <Typography style={{ fontSize: "20px", textAlign: "right", marginTop: "10px" }}>
              備考 : {wheelData.remarks || "-"}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <TableContainer component={Table} sx={{ boxShadow: "none", minWidth: "1600px" }}>
          <TableHead>
            <TableRowComponent data={HEADER_WHEEL_TIRE_EST_PDF} type="header" />
          </TableHead>
          <TableBody>
            {tireList.map((tire, index) => {
              const discountAmount =
                (tire.confirm_total_price ?? 0) + (tire.cb_tire_price ?? 0) - (
                  (wheelData.confirm_total_price ?? 0) +
                  (tire.discounted_sale_price ?? 0) +
                  (tire.wage_cost_price ?? 0) +
                  (tire.puncture_protection_price ?? 0)
                );
              return (
                <Fragment key={`tire-${tire.quote_detail_id || index}`}>
                  <TableRowComponent
                    data={[
                      {
                        label: (
                          <>
                            {tire.maker_name || ""}
                            <br />
                            {tire.item_name || ""}
                          </>
                        ),
                        sx: { textAlign: "left" },
                      },
                      {
                        label: tire.midt_tire_loin_speed_mark || "-",
                        sx: { px: "2px", textAlign: "left" },
                      },
                      {
                        label: tire.tire_label || "-",
                        sx: { px: "2px", textAlign: "left" },
                      },
                      {
                        label: `${tire.quantity || 0}本`,
                      },
                      {
                        label: renderPriceLabel(formatCurrency(tire.nomal_price || 0)),
                        sx: { px: "2px" },
                      },
                      {
                        label: tire.special_discount
                          ? `${tire.special_discount}%`
                          : `${tire.fixed_discount || 0}%`,
                        sx: { px: "2px" },
                      },
                      {
                        label: renderPriceLabel(formatCurrency(tire.discounted_sale_price || 0)),
                        sx: { px: "2px" },
                      },
                      {
                        label: renderPriceLabel(formatCurrency(tire.wage_cost_price || 0)),
                        sx: { px: "2px" },
                      },
                      {
                        label: renderPriceLabel(formatCurrency(tire.puncture_protection_price || 0)),
                        sx: { px: "2px" },
                      },
                      {
                        label: discountAmount !== 0 ? renderPriceLabel(formatCurrency(discountAmount)) : renderPriceLabel(0),
                        sx: { px: "2px" },
                      },
                      {
                        label: renderPriceLabel(formatCurrency(tire.confirm_total_price || 0)),
                        sx: { px: "2px", fontSize: "34px" },
                      },
                      {
                        label: renderPriceLabel(formatCurrency(tire.cb_tire_price || 0)),
                        sx: { px: "2px", fontSize: { xs: 16, md: 18, lg: 20 } },
                      },
                    ]}
                  />
                  <TableRow>
                    <TableCellComponent colSpan={HEADER_EST_PDF.length} hiddenBorder>
                      <Box
                        sx={{
                          display: "flex",
                          width: "100%",
                          justifyContent: "space-between",
                          alignItems: "center",
                          my: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ minWidth: "150px", display: "flex", flexDirection: "column" }}>
                            <Typography style={{ fontSize: "14px", textAlign: "left" }}>タイヤバーコード</Typography>
                            {tire.midt_jan_cd && (
                              <Barcode value={tire.midt_jan_cd} width={195} height={40} />
                            )}
                          </Box>
                          <Typography style={{ fontSize: "16px", textAlign: "right", marginTop: "20px" }}>
                            {getPriceDisplayText(
                              wheelData.discounted_sale_price ?? 0,
                              tire.discounted_sale_price ?? 0,
                            ).text}
                          </Typography>
                        </Box>
                        <Typography style={{ fontSize: "20px", textAlign: "right" }}>
                          備考 : {tire.remarks || "-"}
                        </Typography>
                      </Box>
                    </TableCellComponent>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </TableContainer>
      </Box>
    </Box>
  );
}
